export type CostPoint = {
  label: string
  planned?: number | null
  actual?: number | null
  projected?: number | null
  low?: number | null
  high?: number | null
  earnedValue?: number | null
}

function niceCeil(value: number): number {
  if (value <= 0) return 1
  const magnitude = 10 ** Math.floor(Math.log10(value))
  const steps = [1, 1.25, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10]
  const step = steps.find((s) => s * magnitude >= value) ?? 10
  return step * magnitude
}

function axisLabel(value: number): string {
  if (value === 0) return "0"
  if (value >= 1_000_000) return `${+(value / 1_000_000).toFixed(2)}M`
  if (value >= 1_000) return `${+(value / 1_000).toFixed(0)}K`
  return String(Math.round(value))
}

function money(value: number): string {
  if (value >= 1_000_000) return `$${+(value / 1_000_000).toFixed(2)}M`
  if (value >= 1_000) return `$${+(value / 1_000).toFixed(1)}K`
  return `$${Math.round(value)}`
}

function spanFlat(coords: { x: number; y: number }[], width: number): { x: number; y: number }[] {
  if (coords.length !== 1) return coords
  return [
    { x: 0, y: coords[0].y },
    { x: width, y: coords[0].y },
  ]
}

export function CostChart({
  points,
  budget,
  height = 212,
  bands = 4,
  tickEvery = 1,
  legend = true,
  projectedLabel = "Projected",
}: {
  points: CostPoint[]
  budget?: number | null
  height?: number
  bands?: number
  tickEvery?: number
  legend?: boolean
  projectedLabel?: string
}) {
  if (points.length < 1) {
    return (
      <p className="py-10 text-center text-label text-muted-foreground">
        Not enough data to chart yet.
      </p>
    )
  }

  const vbHeight = height - 4
  const yBase = vbHeight - 1
  const yTop = 1
  const width = 700

  const values = points.flatMap((p) =>
    [p.planned, p.actual, p.projected, p.high, p.earnedValue].filter(
      (v): v is number => typeof v === "number",
    ),
  )
  const maxY = niceCeil(Math.max(...values, budget ?? 0) * 1.02)

  const X = (i: number) => (points.length > 1 ? (i * width) / (points.length - 1) : width / 2)
  const Y = (v: number) => yBase - (v / maxY) * (yBase - yTop)
  const path = (pts: { x: number; y: number }[]) =>
    pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ")

  const planned = spanFlat(
    points
      .map((p, i) => (typeof p.planned === "number" ? { x: X(i), y: Y(p.planned) } : null))
      .filter((p): p is { x: number; y: number } => p !== null),
    width,
  )

  const actualRows = points
    .map((p, i) => ({ i, p }))
    .filter((r) => typeof r.p.actual === "number")
  const actual = actualRows.map((r) => ({ x: X(r.i), y: Y(r.p.actual as number) }))
  const last = actualRows.length ? actualRows[actualRows.length - 1] : undefined

  const earnedValue = spanFlat(
    points
      .map((p, i) => (typeof p.earnedValue === "number" ? { x: X(i), y: Y(p.earnedValue) } : null))
      .filter((p): p is { x: number; y: number } => p !== null),
    width,
  )

  const projRows = points
    .map((p, i) => ({ i, p }))
    .filter((r) => typeof r.p.projected === "number")

  const anchor = last ? [{ x: X(last.i), y: Y(last.p.actual as number) }] : []
  const projected = projRows.length
    ? anchor.concat(projRows.map((r) => ({ x: X(r.i), y: Y(r.p.projected as number) })))
    : []

  const bandRows = projRows.filter(
    (r) => typeof r.p.low === "number" && typeof r.p.high === "number",
  )
  const band = bandRows.length
    ? anchor
        .concat(bandRows.map((r) => ({ x: X(r.i), y: Y(r.p.high as number) })))
        .concat(bandRows.map((r) => ({ x: X(r.i), y: Y(r.p.low as number) })).reverse())
    : []

  const area = last
    ? actual.concat([
        { x: X(last.i), y: yBase },
        { x: 0, y: yBase },
      ])
    : []

  const gridlines = Array.from({ length: bands + 1 }, (_, i) => yTop + ((yBase - yTop) * i) / bands)
  const yLabels = Array.from({ length: bands + 1 }, (_, i) => (maxY * (bands - i)) / bands)
  const ticks = points.filter((_, i) => i % tickEvery === 0)

  const budgetY = typeof budget === "number" && budget > 0 ? Y(budget) : null

  return (
    <div>
      {legend ? (
        <div className="mb-3 flex items-center justify-end gap-3.5 text-meta text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3.5 border-t-[1.5px] border-foreground" />
            Actual
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3.5 border-t-[1.5px] border-dashed border-muted-foreground" />
            Planned
          </span>
          {projected.length ? (
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3.5 border-t-[1.5px] border-dashed border-[var(--accent-whisper)]" />
              {projectedLabel}
            </span>
          ) : null}
          {earnedValue.length ? (
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3.5 border-t-[1.5px] border-dotted border-muted-foreground" />
              Earned value
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="flex gap-2.5">
        <div
          className="num flex w-[34px] shrink-0 flex-col items-end justify-between pt-1 text-micro text-muted-foreground"
          style={{ height: height - 12 }}
        >
          {yLabels.map((v) => (
            <span key={v}>{axisLabel(v)}</span>
          ))}
        </div>

        <div className="min-w-0 flex-1">
          <div className="relative">
            <svg
              viewBox={`0 0 ${width} ${vbHeight}`}
              preserveAspectRatio="none"
              className="block w-full"
              style={{ height }}
            >
              <g stroke="var(--border)">
                {gridlines.map((y) => (
                  <line key={y} x1="0" y1={y} x2={width} y2={y} />
                ))}
              </g>
              {band.length ? (
                <polygon points={path(band)} fill="var(--accent-whisper)" fillOpacity="0.1" />
              ) : null}
              {budgetY !== null ? (
                <line
                  x1="0"
                  y1={budgetY}
                  x2={width}
                  y2={budgetY}
                  stroke="var(--foreground)"
                  strokeOpacity=".35"
                  strokeDasharray="2 3"
                />
              ) : null}
              {planned.length ? (
                <polyline
                  points={path(planned)}
                  fill="none"
                  stroke="var(--muted-foreground)"
                  strokeWidth="1.25"
                  strokeDasharray="4 4"
                  vectorEffect="non-scaling-stroke"
                />
              ) : null}
              {area.length ? (
                <polygon points={path(area)} fill="var(--foreground)" fillOpacity="0.06" />
              ) : null}
              {projected.length ? (
                <polyline
                  points={path(projected)}
                  fill="none"
                  stroke="var(--accent-whisper)"
                  strokeWidth="1.5"
                  strokeDasharray="4 3"
                  vectorEffect="non-scaling-stroke"
                />
              ) : null}
              {actual.length ? (
                <polyline
                  points={path(actual)}
                  fill="none"
                  stroke="var(--foreground)"
                  strokeWidth="1.75"
                  vectorEffect="non-scaling-stroke"
                />
              ) : null}
              {earnedValue.length ? (
                <polyline
                  points={path(earnedValue)}
                  fill="none"
                  stroke="var(--muted-foreground)"
                  strokeWidth="1.5"
                  strokeDasharray="1 3"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              ) : null}
            </svg>

            {budgetY !== null ? (
              <span
                className="absolute right-0.5 bg-background px-1 text-micro text-muted-foreground"
                style={{ top: (budgetY / vbHeight) * height - 14 }}
              >
                Budget ceiling {money(budget as number)}
              </span>
            ) : null}
            {last ? (
              <span
                className="absolute -ml-[3.5px] -mt-[3.5px] h-[7px] w-[7px] rounded-full bg-foreground"
                style={{
                  left: `${((X(last.i) / width) * 100).toFixed(2)}%`,
                  top: (Y(last.p.actual as number) / vbHeight) * height,
                }}
              />
            ) : null}
          </div>

          <div className="mt-1.5 flex justify-between text-micro text-muted-foreground">
            {ticks.map((t, i) => (
              <span key={`${t.label}-${i}`}>{t.label}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
